import { AlertTriangle, CheckCircle2, Info, WifiOff, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ICON } from "./status.tsx";

/** A real radio inside a selectable card; the label and description are its accessible name. */
export function RadioCard({
  name,
  value,
  checked,
  disabled = false,
  label,
  description,
  onChange,
}: {
  name: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  label: string;
  description?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className={`radio-card${checked ? " checked" : ""}${disabled ? " disabled" : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span className="radio-text">
        <span className="row-title">{label}</span>
        {description && <span className="row-detail">{description}</span>}
      </span>
    </label>
  );
}

const BANNER_ICON = { info: Info, good: CheckCircle2, wait: WifiOff, bad: AlertTriangle } as const;

/** Offline / server / failure notice with a recovery action. Failures are announced at once. */
export function Banner({
  tone,
  message,
  action,
}: {
  tone: "info" | "good" | "wait" | "bad";
  message: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  const Icon = BANNER_ICON[tone];
  return (
    <div className={`banner ${tone}`} role={tone === "bad" || tone === "wait" ? "alert" : "status"}>
      <Icon {...ICON} aria-hidden="true" />
      <span className="banner-text">{message}</span>
      {action && (
        <button type="button" className="button small" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Modal confirmation. Focus starts on Cancel (the safe choice); Escape cancels. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);
  if (!open) return null;
  return (
    <div className="backdrop">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(body ? { "aria-describedby": bodyId } : {})}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      >
        <h2 id={titleId}>{title}</h2>
        {body && <div id={bodyId}>{body}</div>}
        <div className="dialog-actions">
          <button ref={cancelRef} type="button" className="button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`button ${destructive ? "danger" : "primary"}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

type ToastItem = { id: number; text: string };
const ToastContext = createContext<(text: string) => void>(() => undefined);
const TOAST_MS = 5000;

/** Non-intrusive messages in one polite live region, removed after a few seconds. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(0);
  const push = useCallback((text: string) => {
    const id = ++next.current;
    setItems((xs) => [...xs, { id, text }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), TOAST_MS);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="toast">
            {t.text}
            <button
              type="button"
              className="icon-button"
              aria-label="Dismiss"
              onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}
            >
              <X {...ICON} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
